import { createContext, useContext, useState, useEffect, useLayoutEffect, useCallback } from 'react';
import api from '../utils/axiosConfig';
import { useAuth } from './AuthContext';

const ThemeContext = createContext();

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }) => {
  const { isAuthenticated, loading: authLoading } = useAuth();

  const [mode, setMode] = useState(() => {
    const saved = localStorage.getItem('citius-theme');
    if (saved) {
      const [m] = saved.split('-');
      return m || 'light';
    }
    // No saved preference: dark for anonymous visitors; corrected after auth resolves if logged in.
    return 'dark';
  });

  const [scheme, setScheme] = useState(() => {
    const saved = localStorage.getItem('citius-theme');
    if (saved) {
      const parts = saved.split('-');
      return parts[1] || 'blue';
    }
    return 'blue';
  });

  useLayoutEffect(() => {
    if (authLoading) return;
    const saved = localStorage.getItem('citius-theme');
    if (saved) return;
    if (isAuthenticated) {
      setMode('light');
    }
  }, [authLoading, isAuthenticated]);

  const applyTheme = useCallback((m, s) => {
    const root = document.documentElement;
    root.classList.remove('dark', 'light');
    root.classList.add(m);

    const themeClasses = Array.from(root.classList).filter(c => c.startsWith('theme-'));
    themeClasses.forEach(c => root.classList.remove(c));

    if (s !== 'blue') {
      root.classList.add(`theme-${s}`);
    }
  }, []);

  useEffect(() => {
    applyTheme(mode, scheme);
    const themeString = `${mode}-${scheme}`;
    localStorage.setItem('citius-theme', themeString);
  }, [mode, scheme, applyTheme]);

  const setTheme = useCallback((newMode, newScheme) => {
    if (newMode) setMode(newMode);
    if (newScheme) setScheme(newScheme);

    const themeString = `${newMode || mode}-${newScheme || scheme}`;
    api.put('/api/users/theme', { themePreference: themeString }).catch(() => {});
  }, [mode, scheme]);

  const toggleMode = useCallback(() => {
    const newMode = mode === 'dark' ? 'light' : 'dark';
    setTheme(newMode, scheme);
  }, [mode, scheme, setTheme]);

  return (
    <ThemeContext.Provider
      value={{ mode, scheme, setTheme, toggleMode }}
    >
      {children}
    </ThemeContext.Provider>
  );
};
